// AI-882 — pure geometry + easing for the unified Tarot Card Stage.
//
// The stage drives every card's transform from a single rAF loop
// (`useTarotStageAnimation`). This module holds the PURE math it needs: fan-arc
// layout, per-card resting/lifted/flying offsets, and easing curves. Keeping it
// pure (no DOM, no React) makes the load-bearing geometry unit-testable, while
// the rAF/DOM writes stay in the hook (verified in-browser per OraclePage
// CLAUDE.md).
//
// Values mirror the legacy `TarotPickACard` constants so the fan looks
// identical; the difference is only that the loop interpolates them every frame
// instead of CSS transitions snapping between them.

// Fan card counts (desktop fans more; mobile fewer so tap targets don't overlap).
export const NUM_CARDS_DESKTOP = 16
export const NUM_CARDS_MOBILE = 12
export const MOBILE_BREAKPOINT_PX = 768

// Total fan arc sweep, and the per-card-edge margin that caps the arc radius so
// the outermost (rotation-widened) card never clips the viewport.
export const FAN_THETA_DEG = 24
export const FAN_EDGE_MARGIN_PX = 40

// Lift distances (px) measured along a card's surface normal. Hover lifts half
// of the pick lift, matching the legacy OFFSET_PX / 2 vs OFFSET_PX.
export const HOVER_LIFT_PX = 50
export const PICK_LIFT_PX = 100

// Transition durations (ms) — mirror the legacy PICK_A_CARD_T_* constants so the
// feel is preserved.
export const FLAY_MS = 200 // stacked → fanned-out
export const HOVER_MS = 100 // hover lift / settle
export const PICK_MS = 150 // waiting → picked-lift (and others fade out)
export const HANG_MS = 300 // picked-lift hold before the fly begins
export const FLY_MS = 600 // picked card flies to the jiggle target (== FLYING_MS)

// Pre-measurement fallback for the single render before window.innerWidth is read.
export const FALLBACK_VIEWPORT_PX = 320

// ─── Shuffle riffle ──────────────────────────────────────────────────────────
// The same N fan-cards riffle as one stack (split/merge of two halves) before
// fanning out. Driven continuously by elapsed time so the cards' positions are
// known at any instant — the shuffle→pick handoff then tweens from the LIVE
// riffle pose into the fan (no snap, no blank frame; AI-882 issue 3). Tunable.
export const SHUFFLE_PERIOD_MS = 1500 // one split→merge cycle
export const SHUFFLE_STAGGER = 0.03 // per-card phase offset → riffle cascade
export const SHUFFLE_SPLIT_FRAC = 0.55 // peak split, as a fraction of card width
export const SHUFFLE_LIFT_FRAC = 0.05 // peak lift, as a fraction of card height
export const SHUFFLE_TILT_DEG = 5 // peak z-tilt of a split half

// ─── Jiggle + flip ───────────────────────────────────────────────────────────
// The chosen card flies to center (rotateY 0), then jiggles about the y-axis as
// a continuous sine STARTING AT 0° — so it's continuous with the fly's end and
// never snaps to the legacy keyframe's -40° start (AI-882 issue 4). When the FSM
// advances to `flip`, the loop reads the live sine angle and eases it to 180°
// (AI-882 issue 5). Amplitude/period are tunable; feel is verified in-browser.
export const JIGGLE_AMPLITUDE_DEG = 40 // matches the legacy ±40° swing
export const JIGGLE_PERIOD_MS = 3000 // one full 0→+A→0→-A→0 oscillation
// Flip duration (back→front rotateY 0/live→180 + fly-to-landing). Mirrors the
// legacy useTarotFlyAnimation default so the feel is preserved.
export const FLIP_MS = 2000

// Reveal overlap: how long the landed flip overlay is held opaque AFTER the FSM
// reaches `tarot_idle`. At tarot_idle the structured card is un-hidden and fades
// in (its `__content` over ~300–500ms, its freshly-decoding `<img>` over 300ms).
// The overlay carries the SAME, already-decoded front image, so holding it over
// that fade prevents the white gap that appears if it unmounts the instant the
// structured card begins painting. Must cover the structured fade durations.
export const REVEAL_OVERLAP_MS = 500

const THETA_RAD = (FAN_THETA_DEG * Math.PI) / 180

export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

// Container-relative offset of a card's bottom-center from the fan origin, plus
// its z-rotation. The stage's fan container is fixed at (fanOriginX, fanOriginY)
// and each card is drawn at left:-w/2, top:-h with transform-origin bottom-center
// — so (dx, dy) is exactly the card's bottom-center offset.
export interface CardOffset {
  dx: number
  dy: number
  rotateZ: number
}

export interface FanLayout {
  numCards: number
  cardWidth: number
  cardHeight: number
  fanOriginX: number
  fanOriginY: number
  radius: number
  /** Horizontal offset from fan origin to the jiggle-target center; the picked
   *  card translates here on the fly so it lands exactly on the target rect. */
  landingDx: number
}

/** Bottom-center offset (from the fan origin) plus the size scale a card needs
 *  to overlay a destination rect. The card's transform-origin is bottom-center,
 *  so scaling about it and translating its bottom-center to the rect's
 *  bottom-center makes the scaled card cover the rect exactly. */
export interface LandingOffset {
  dx: number
  dy: number
  scaleX: number
  scaleY: number
}

/** Ease-in-out: 3t² − 2t³ (same curve as useTarotFlyAnimation). */
export const easeInOut = (t: number): number => t * t * (3 - 2 * t)

/** Ease-out cubic: 1 − (1−t)³ — approximates CSS `ease-out`. */
export const easeOut = (t: number): number => 1 - (1 - t) ** 3

export const clamp01 = (t: number): number => {
  if (t < 0) return 0
  if (t > 1) return 1
  return t
}

export const lerp = (from: number, to: number, t: number): number => from + (to - from) * t

export const getNumCards = (viewportWidth: number): number => (
  viewportWidth >= MOBILE_BREAKPOINT_PX ? NUM_CARDS_DESKTOP : NUM_CARDS_MOBILE
)

/**
 * Compute the fan layout for a measured viewport and a card-sized jiggle target.
 * Ported from TarotPickACard: the arc spans ~80% of the viewport but is capped so
 * the outermost card's rotation-widened bounding box stays FAN_EDGE_MARGIN_PX
 * inside each edge.
 */
export const getFanLayout = (viewportWidth: number, jiggleTarget: Rect): FanLayout => {
  const effectiveWidth = viewportWidth > 0 ? viewportWidth : FALLBACK_VIEWPORT_PX
  const cardWidth = jiggleTarget.width
  const cardHeight = jiggleTarget.height
  const numCards = getNumCards(effectiveWidth)

  const fanOriginX = effectiveWidth / 2
  const fanOriginY = jiggleTarget.top + jiggleTarget.height
  const landingDx = (jiggleTarget.left + jiggleTarget.width / 2) - fanOriginX

  const sinHalfTheta = Math.sin(THETA_RAD / 2)
  const cosHalfTheta = Math.cos(THETA_RAD / 2)
  const cardBboxHalfExtent = (cardWidth / 2) * cosHalfTheta + cardHeight * sinHalfTheta
  const idealRadius = (effectiveWidth * 0.4) / sinHalfTheta
  const maxRadius = Math.max(
    0,
    (effectiveWidth / 2 - cardBboxHalfExtent - FAN_EDGE_MARGIN_PX) / sinHalfTheta,
  )
  const radius = Math.min(idealRadius, maxRadius)

  return {
    numCards, cardWidth, cardHeight, fanOriginX, fanOriginY, radius, landingDx,
  }
}

/** Angle (radians) of card `i` within the fan, centered on 0. */
export const getCardAngleRad = (i: number, numCards: number): number => {
  const t = numCards > 1 ? i / (numCards - 1) : 0.5
  return (t - 0.5) * THETA_RAD
}

/**
 * Resting fan offset for card `i`, with an optional outward hover lift
 * (`hoverFraction` 0→1) along the card's surface normal.
 */
export const getFanOffset = (
  i: number,
  layout: FanLayout,
  hoverFraction = 0,
): CardOffset => {
  const alpha = getCardAngleRad(i, layout.numCards)
  const lift = HOVER_LIFT_PX * hoverFraction
  return {
    dx: layout.radius * Math.sin(alpha) + lift * Math.sin(alpha),
    dy: layout.radius * (1 - Math.cos(alpha)) - lift * Math.cos(alpha),
    rotateZ: (alpha * 180) / Math.PI,
  }
}

/** Lifted (picked / hanging) offset for card `i` — pulled PICK_LIFT_PX along its normal. */
export const getPickedLiftOffset = (i: number, layout: FanLayout): CardOffset => {
  const alpha = getCardAngleRad(i, layout.numCards)
  return {
    dx: (layout.radius + PICK_LIFT_PX) * Math.sin(alpha),
    dy: layout.radius * (1 - Math.cos(alpha)) - PICK_LIFT_PX * Math.cos(alpha),
    rotateZ: (alpha * 180) / Math.PI,
  }
}

/** Flying target: bottom-center at the jiggle-target center, upright. */
export const getFlyingOffset = (layout: FanLayout): CardOffset => ({
  dx: layout.landingDx,
  dy: 0,
  rotateZ: 0,
})

/**
 * Riffle pose of card `i` at `elapsedMs`. The deck splits into a left half
 * (lower indices) and a right half, each separating and merging on a smooth
 * cosine envelope, staggered per card so it cascades like a riffle. Returns the
 * container-relative offset; at envelope 0 the cards stack at the origin.
 */
export const getShuffleOffset = (
  i: number,
  layout: FanLayout,
  elapsedMs: number,
): CardOffset => {
  const { numCards, cardWidth, cardHeight } = layout
  const leftCount = Math.ceil(numCards / 2)
  const isLeft = i < leftCount
  const half = isLeft ? -1 : 1
  const idxInHalf = isLeft ? i : i - leftCount

  const phase = (elapsedMs % SHUFFLE_PERIOD_MS) / SHUFFLE_PERIOD_MS
  const local = phase - idxInHalf * SHUFFLE_STAGGER
  // Smooth, periodic 0→1→0 envelope (no wrap discontinuity).
  const env = (1 - Math.cos(2 * Math.PI * local)) / 2

  return {
    dx: half * env * cardWidth * SHUFFLE_SPLIT_FRAC,
    dy: -env * cardHeight * SHUFFLE_LIFT_FRAC,
    rotateZ: half * env * SHUFFLE_TILT_DEG,
  }
}

/**
 * Live jiggle rotation (degrees about the y-axis) at `elapsedMs` into the jiggle.
 * A pure sine so it starts at 0° (continuous with the fly's upright end → no snap,
 * AI-882 issue 4) and the flip can read its exact value at any instant.
 */
export const getJiggleRotateY = (elapsedMs: number): number => (
  JIGGLE_AMPLITUDE_DEG * Math.sin((2 * Math.PI * elapsedMs) / JIGGLE_PERIOD_MS)
)

/**
 * Where the picked card must translate+scale to overlay `landingRect`. With the
 * card sized to the jiggle target and anchored bottom-center at the fan origin,
 * we move its bottom-center to the landing's bottom-center and scale to the
 * landing, so the flip lands on `[data-oracle-card-landing]`, re-measured each
 * frame for autoscroll (AI-882 issue 5).
 *
 * The scale is UNIFORM (the smaller of the two axis ratios), NOT independent
 * scaleX/scaleY (AI-823). The landing rect is the structured card's
 * `.divination-card-renderer__image-container`, whose `max-height: 60vh` cap can
 * render it WIDER than the card's 250:400 aspect. The card image itself is
 * 250:400 and the structured `<img>` shows it with `object-fit: contain` (so it
 * stays 250:400, centered, with empty sides). Scaling each axis independently to
 * the clamped box would stretch the overlay's image wider than the revealed
 * card. Taking the min reproduces `contain`: bottom-center alignment then leaves
 * the overlay centered and bottom-aligned in the box — identical to the revealed
 * image whenever the container is ≥ the card's aspect (the only case the 60vh cap
 * produces; an exactly-aspect or unclamped container yields scaleX == scaleY, so
 * this is a no-op there).
 */
export const getLandingOffset = (layout: FanLayout, landingRect: Rect): LandingOffset => {
  const scale = Math.min(
    landingRect.width / layout.cardWidth,
    landingRect.height / layout.cardHeight,
  )
  return {
    dx: (landingRect.left + landingRect.width / 2) - layout.fanOriginX,
    dy: (landingRect.top + landingRect.height) - layout.fanOriginY,
    scaleX: scale,
    scaleY: scale,
  }
}
